import { Trophy } from "lucide-react";
import StandingsTable from "@/components/betmodel/StandingsTable";
import { useState, useEffect } from "react";
import { useTranslation } from "@/lib/i18n";
import {
  ArrowLeft, TrendingUp, Target, CornerDownRight, Lock, Unlock,
  Eye, Award, Loader2, Activity, Users, Zap, Shield,
} from "lucide-react";
import { api } from "@/lib/api";

function ProbBar({ label, value, color = "bg-volt" }) {
  const pct = Math.round(value * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between items-center">
        <span className="text-xs font-bold uppercase tracking-widest text-txt-secondary">{label}</span>
        <span className="font-mono text-sm font-bold text-white">{pct}%</span>
      </div>
      <div className="h-2 bg-obsidian rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full transition-all duration-700`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function EventIcon({ type, detail }) {
  if (type === "Goal") return <span className="text-lg">⚽</span>;
  if (type === "Card" && detail === "Yellow Card") return <span className="text-lg">🟨</span>;
  if (type === "Card" && detail === "Red Card") return <span className="text-lg">🟥</span>;
  if (type === "subst") return <span className="text-lg">🔄</span>;
  if (type === "Var") return <span className="text-lg">📺</span>;
  return <span className="text-lg">•</span>;
}

function StatBar({ label, homeVal, awayVal }) {
  const home = parseInt(homeVal) || 0;
  const away = parseInt(awayVal) || 0;
  const total = home + away || 1;
  const homePct = Math.round((home / total) * 100);
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-txt-secondary">
        <span className="font-mono font-bold text-white">{homeVal ?? 0}</span>
        <span className="text-[10px] uppercase tracking-wider">{label}</span>
        <span className="font-mono font-bold text-white">{awayVal ?? 0}</span>
      </div>
      <div className="flex h-1.5 rounded-full overflow-hidden bg-obsidian">
        <div className="bg-volt transition-all duration-700" style={{ width: `${homePct}%` }} />
        <div className="bg-danger transition-all duration-700" style={{ width: `${100 - homePct}%` }} />
      </div>
    </div>
  );
}

const LEAGUE_NAME_TO_CODE = {
  "Primeira Liga (PT)": "POR",
  "Premier League": "ENG",
  "La Liga": "SPA",
  "Bundesliga": "GER",
  "Serie A": "ITA",
  "Ligue 1": "FRA",
  "Eredivisie": "NED",
  "Pro League (BE)": "BEL",
  "Super Lig": "TUR",
  "Premiership (SC)": "SCO",
  "Brasileirao S.A": "BRA",
  "Liga Profesional": "ARG",
  "Liga dos Campeões": "UCL",
  "Liga Europa": "UEL",
  "Conference League": "UCO",
  "Taça de Portugal": "TCP",
  "FA Cup": "FAC",
  "Copa del Rey": "CDR",
  "Coupe de France": "CDF",
  "DFB Pokal": "DFB",
  "Coppa Italia": "CPI",
};

export default function MatchPrediction({ prediction, onBack, bankroll, isAdmin, leagueCode }) {
  const { t, lang } = useTranslation();
  const [unlocked, setUnlocked] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [liveData, setLiveData] = useState(null);
  const [loadingLive, setLoadingLive] = useState(false);
  const [activeTab, setActiveTab] = useState("prediction");
  const [showStandings, setShowStandings] = useState(false);
  
if (!prediction) return null;

  const { probabilities, suggestions, expected_goals, expected_corners, home, away, fixture, odds } = prediction;
  const effectiveUnlocked = unlocked || isAdmin;
  const valueBets = suggestions?.filter((s) => s.is_value_bet) || [];
  const normalBets = suggestions?.filter((s) => !s.is_value_bet) || [];
  const isLiveOrFinished = ["1H","2H","HT","ET","BT","P","SUSP","INT","LIVE","FT","AET","PEN"].includes(fixture?.status);

  useEffect(() => {
    if (isLiveOrFinished && fixture?.id) {
      setLoadingLive(true);
      api.getLiveData(fixture.id)
        .then(data => setLiveData(data))
        .catch(() => {})
        .finally(() => setLoadingLive(false));
    }
  }, [fixture?.id, isLiveOrFinished]);

  const handleUnlock = () => {
    setUnlocking(true);
    setTimeout(() => { setUnlocking(false); setUnlocked(true); }, 3000);
  };

  const marketLabels = {
    "1": t('homeWinFull'), "X": t('drawFull'), "2": t('awayWinFull'),
    "over25": t('over25Full'), "under25": t('under25Full'),
    "btts_yes": t('bttsYes'), "btts_no": t('bttsNo'),
    "corners_over95": `${t('cornersOver')} 9.5`,
  };

  const tabs = [{ id: "prediction", label: t('prediction') }];
  if (isLiveOrFinished) {
    tabs.push({ id: "events", label: t('events') });
    tabs.push({ id: "stats", label: t('statistics') });
    tabs.push({ id: "lineups", label: t('lineups') });
  }

  const getStatValue = (stats, type) => {
    const stat = stats?.find(s => s.type === type);
    return stat?.value ?? 0;
  };

  return (
    <div className="space-y-6 animate-slide-up" data-testid="match-prediction">
      {/* Header */}
     <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 bg-surface border border-line rounded-sm hover:bg-surface-hover transition-colors">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div>
            <h2 className="font-heading text-2xl font-black uppercase tracking-tight">
              {home?.name || "Casa"} vs {away?.name || "Fora"}
            </h2>
            <p className="text-xs text-txt-secondary">
              {fixture?.date ? new Date(fixture.date).toLocaleDateString(lang || "pt-PT", {
                weekday: "long", day: "2-digit", month: "long", hour: "2-digit", minute: "2-digit"
              }) : ""}
              {fixture?.venue ? ` | ${fixture.venue}` : ""}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowStandings(!showStandings)}
          className={`flex items-center gap-1.5 px-3 py-2 bg-surface border rounded-sm text-xs font-bold uppercase tracking-wider transition-colors shrink-0 ${showStandings ? "border-volt text-volt" : "border-line text-txt-secondary hover:text-white"}`}
        >
          <Trophy className="w-3.5 h-3.5" />
          <span>{t('standings')}</span>
        </button>
      </div>

      {/* Team Logos + Score */}
      <div className="bg-surface border border-line rounded-sm p-6">
        <div className="flex items-center justify-center gap-8">
          <div className="text-center space-y-2">
            {home?.logo && <img src={home.logo} alt="" className="w-14 h-14 mx-auto object-contain" />}
            <p className="text-sm font-semibold truncate max-w-[120px]">{home?.name}</p>
          </div>
          <div className="text-center">
            {isLiveOrFinished && (liveData?.goals || prediction.goals) ? (
              <div className="font-mono text-4xl font-bold text-white">
                {(liveData?.goals?.home ?? prediction.goals?.home ?? 0)} - {(liveData?.goals?.away ?? prediction.goals?.away ?? 0)}
              </div>
            ) : (
              <div className="font-mono text-4xl font-bold text-volt">
                {expected_goals?.home || "?"} - {expected_goals?.away || "?"}
              </div>
            )}
            <p className="text-[10px] uppercase tracking-widest text-txt-secondary mt-1">
              {isLiveOrFinished ? t('result') : t('expectedGoals')}
            </p>
            {isLiveOrFinished && (
              <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm mt-1 inline-block ${
                (liveData?.status || fixture?.status) === "FT" ? "bg-txt-secondary/20 text-txt-secondary" : "bg-volt/20 text-volt"
              }`}>
                {liveData?.elapsed ? `${liveData.elapsed}'` : (liveData?.status || fixture?.status)}
              </span>
            )}
          </div>
          <div className="text-center space-y-2">
            {away?.logo && <img src={away.logo} alt="" className="w-14 h-14 mx-auto object-contain" />}
            <p className="text-sm font-semibold truncate max-w-[120px]">{away?.name}</p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      {tabs.length > 1 && (
        <div className="flex gap-1 bg-surface border border-line rounded-sm p-1">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-sm transition-colors ${
                activeTab === tab.id ? "bg-volt/10 text-volt" : "text-txt-secondary hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      )}


 
{showStandings && (
  <StandingsTable
    leagueCode={leagueCode || LEAGUE_NAME_TO_CODE[prediction?.league] || "POR"}
          leagueName={prediction?.league}
  
  

          onClose={() => setShowStandings(false)}
        />
      )}

      {/* Tab: Prediction */}
      {activeTab === "prediction" && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-surface border border-line rounded-sm p-5 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <Target className="w-4 h-4 text-volt" />
                <h3 className="font-heading text-sm font-bold uppercase tracking-widest">{t('result')}</h3>
              </div>
              <ProbBar label={t('home1')} value={probabilities?.["1"] || 0} color="bg-volt" />
              <ProbBar label={t('drawX')} value={probabilities?.["X"] || 0} color="bg-azure" />
              <ProbBar label={t('away2')} value={probabilities?.["2"] || 0} color="bg-danger" />
            </div>
            <div className="bg-surface border border-line rounded-sm p-5 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="w-4 h-4 text-volt" />
                <h3 className="font-heading text-sm font-bold uppercase tracking-widest">{t('goals')}</h3>
              </div>
              <ProbBar label={t('over25')} value={probabilities?.over25 || 0} color="bg-volt" />
              <ProbBar label={t('under25')} value={probabilities?.under25 || 0} color="bg-azure" />
              <ProbBar label={t('bttsShort')} value={probabilities?.btts_yes || 0} color="bg-warning" />
            </div>
            <div className="bg-surface border border-line rounded-sm p-5 space-y-3">
              <div className="flex items-center gap-2 mb-1">
                <CornerDownRight className="w-4 h-4 text-volt" />
                <h3 className="font-heading text-sm font-bold uppercase tracking-widest">{t('corners')}</h3>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-txt-secondary">{t('totalExpected')}:</span>
                <span className="font-mono font-bold text-volt">{expected_corners?.total || "?"}</span>
              </div>
              <ProbBar label={t('over85c')} value={probabilities?.corners_over85 || 0} color="bg-volt" />
              <ProbBar label={t('over95c')} value={probabilities?.corners_over95 || 0} color="bg-azure" />
              <ProbBar label={t('over105c')} value={probabilities?.corners_over105 || 0} color="bg-warning" />
            </div>
            <div className="bg-surface border border-line rounded-sm p-5 space-y-2">
              <div className="flex items-center gap-2 mb-1">
                <Award className="w-4 h-4 text-volt" />
                <h3 className="font-heading text-sm font-bold uppercase tracking-widest">{t('model')}</h3>
              </div>
              <div className="space-y-1.5 text-sm">
                <div className="flex justify-between">
                  <span className="text-txt-secondary">{t('version')}</span>
                  <span className="font-mono text-xs">{prediction?.model_version}</span>
                </div>
                <div className="flex justify-between">
                 <span className="text-txt-secondary">{t('league')}</span>
                  <span className="font-mono text-xs">{prediction?.league}</span>
                </div>
                <div className="flex justify-between">
                 <span className="text-txt-secondary">{t('threshold')}</span>
                  <span className="font-mono text-xs text-warning">{prediction?.value_threshold || "6.5%"}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-txt-secondary">{t('bankroll')}</span>
                  <span className="font-mono text-xs">{bankroll}EUR</span>
                </div>
              </div>
            </div>
          </div>

          {normalBets.length > 0 && (
            <div className="bg-surface border border-line rounded-sm p-5">
              <h3 className="font-heading text-sm font-bold uppercase tracking-widest mb-3">{t('marketAnalysis')}</h3>
              <div className="space-y-2">
                {normalBets.map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-line/30 last:border-0">
                    <div>
                      <span className="text-sm font-medium">{marketLabels[s.market] || s.market}</span>
                      <span className="text-xs text-txt-secondary ml-2">@ {s.odd}</span>
                    </div>
                    <span className={`font-mono text-sm ${s.edge > 0 ? "text-volt" : "text-danger"}`}>
                      {s.edge > 0 ? "+" : ""}{s.edge}%
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {valueBets.length > 0 && (
            <div className="relative">
              <div className={`bg-surface border rounded-sm p-5 ${effectiveUnlocked ? "border-volt" : "border-line"}`}>
                <div className="flex items-center gap-2 mb-3">
                  {effectiveUnlocked ? <Unlock className="w-4 h-4 text-volt" /> : <Lock className="w-4 h-4 text-warning" />}
                  <h3 className="font-heading text-sm font-bold uppercase tracking-widest">Value Bets</h3>
                </div>
                <div className={effectiveUnlocked ? "" : "value-blur"}>
                  <div className="space-y-3">
                    {valueBets.map((s, i) => (
                      <div key={i} className="flex items-center justify-between p-3 bg-obsidian rounded-sm border border-line/30">
                        <div>
                          <p className="font-semibold text-sm">{marketLabels[s.market] || s.market}</p>
                          <p className="text-xs text-txt-secondary">Prob: {s.probability}% | Odd: {s.odd}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono text-lg font-bold text-volt">+{s.edge}%</p>
                          <p className="text-xs text-volt">Stake: {s.stake}EUR</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                {!effectiveUnlocked && (
                  <div className="absolute inset-0 flex items-center justify-center rounded-sm"
                    style={{ background: "linear-gradient(180deg, rgba(10,10,10,0.1) 0%, rgba(10,10,10,0.95) 100%)" }}>
                    <div className="flex flex-col items-center gap-3">
                      <button onClick={() => {
                        window.open('https://omg10.com/4/11029694', '_blank');
                        setTimeout(() => setUnlocked(true), 5000);
                      }}
                        className="bg-volt text-obsidian font-bold uppercase px-8 py-3 rounded-sm hover:bg-volt/80 transition-all flex items-center gap-2 text-sm">
                        🔓 FREE — Watch Ad
                      </button>
                      <button onClick={() => window.open('https://whop.com/betmodel/betmodel-premium/', '_blank')}
                        className="text-txt-secondary text-xs hover:text-volt transition-colors">
                        or Subscribe $9.99/month →
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}

      {/* Tab: Events */}
      {activeTab === "events" && (
        <div className="bg-surface border border-line rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-line flex items-center gap-2">
            <Activity className="w-4 h-4 text-volt" />
            <h3 className="font-heading text-sm font-bold uppercase tracking-widest">{t('matchEvents')}</h3>
          </div>
          {loadingLive ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 text-volt animate-spin" /></div>
          ) : liveData?.events?.length === 0 ? (
            <p className="text-txt-secondary text-sm text-center py-6">{t('noEvents')}</p>
          ) : (
            <div className="divide-y divide-line/30">
              {liveData?.events?.filter(e => e.type !== "subst").map((event, i) => (
                <div key={i} className="flex items-center gap-3 px-4 py-3">
                  <span className="font-mono text-xs text-txt-secondary w-8">{event.time?.elapsed}'</span>
                  <EventIcon type={event.type} detail={event.detail} />
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{event.player?.name || event.detail}</p>
                    {event.assist?.name && <p className="text-xs text-txt-secondary">Assist: {event.assist.name}</p>}
                  </div>
                  <img src={event.team?.logo} alt="" className="w-5 h-5 object-contain" />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Tab: Statistics */}
      {activeTab === "stats" && (
        <div className="bg-surface border border-line rounded-sm overflow-hidden">
          <div className="px-4 py-3 border-b border-line flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-volt" />
              <h3 className="font-heading text-sm font-bold uppercase tracking-widest">{t('statistics')}</h3>
            </div>
            <div className="flex items-center gap-4 text-xs font-bold">
              <span className="text-volt">{home?.name}</span>
              <span className="text-danger">{away?.name}</span>
            </div>
          </div>
          {loadingLive ? (
            <div className="flex items-center justify-center py-8"><Loader2 className="w-6 h-6 text-volt animate-spin" /></div>
          ) : (
            <div className="p-4 space-y-3">
              {liveData?.statistics?.[0] && (() => {
                const homeStats = liveData.statistics[0]?.statistics || [];
                const awayStats = liveData.statistics[1]?.statistics || [];
                const statTypes = ["Ball Possession","Shots on Goal","Total Shots","Corner Kicks","Fouls","Yellow Cards","Offsides","Passes accurate"];
                return statTypes.map(type => (
                  <StatBar
                    key={type}
                    label={type}
                    homeVal={getStatValue(homeStats, type)}
                    awayVal={getStatValue(awayStats, type)}
                  />
                ));
              })()}
            </div>
          )}
        </div>
      )}

      {/* Tab: Lineups */}
      {activeTab === "lineups" && (
        <div className="grid grid-cols-2 gap-3">
          {loadingLive ? (
            <div className="col-span-2 flex items-center justify-center py-8"><Loader2 className="w-6 h-6 text-volt animate-spin" /></div>
          ) : liveData?.lineups?.map((lineup, i) => (
            <div key={i} className="bg-surface border border-line rounded-sm overflow-hidden">
              <div className="px-3 py-2 border-b border-line flex items-center gap-2">
                <img src={lineup.team?.logo} alt="" className="w-5 h-5 object-contain" />
                <div>
                  <p className="text-xs font-bold">{lineup.team?.name}</p>
                  <p className="text-[10px] text-txt-secondary">{lineup.formation}</p>
                </div>
              </div>
              <div className="p-2 space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-volt px-2 py-1">{t('starters')}</p>
                {lineup.startXI?.map((p, j) => (
                  <div key={j} className="flex items-center gap-2 px-2 py-1">
                    <span className="font-mono text-[10px] text-txt-secondary w-4">{p.player?.number}</span>
                    <span className="text-xs">{p.player?.name}</span>
                    <span className="text-[10px] text-txt-secondary ml-auto">{p.player?.pos}</span>
                  </div>
                ))}
                <p className="text-[10px] font-bold uppercase tracking-widest text-txt-secondary px-2 py-1 mt-2">{t('substitutes')}</p>
                {lineup.substitutes?.map((p, j) => (
                  <div key={j} className="flex items-center gap-2 px-2 py-1">
                    <span className="font-mono text-[10px] text-txt-secondary w-4">{p.player?.number}</span>
                    <span className="text-xs text-txt-secondary">{p.player?.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
